import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoginWithPassword } from '@data';
import { CredentialsForm } from './CredentialsForm';

export interface PasswordLoginFormProps {
  onSuccess: () => void;
}

// The real email/password sign-in (POST /auth/login). This is a production login path for everyone,
// not dev-only.
export const PasswordLoginForm = ({ onSuccess }: PasswordLoginFormProps): ReactElement => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutate: loginWithPassword, isPending } = useLoginWithPassword();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    loginWithPassword(
      { email, password },
      {
        onSuccess: () => onSuccess(),
        onError: err => {
          // The API tags a refused sign-in with a `reason`; a deactivated account gets its own message
          // so the person doesn't keep retrying a password that is actually correct.
          const reason = (err as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
          setError(reason === 'userDeactivated' ? t('login.accountDeactivated') : t('login.invalidCredentials'));
        },
      },
    );
  };

  return (
    <CredentialsForm
      email={email}
      password={password}
      setEmail={setEmail}
      setPassword={setPassword}
      onSubmit={handleSubmit}
      submitLabel={t('login.logIn')}
      isPending={isPending}
      error={error}
      emailId="login-email"
      passwordId="login-password"
      showForgotPassword
    />
  );
};

export default PasswordLoginForm;
